/**
 * What the pre-auth pages draw around the sign-in button, derived from the
 * tenant's `loginPage` options.
 *
 * Re-validated on read even though the server already normalised the values:
 * these become real `href`s on a public page, and a `loginPage` column can be
 * written by hand. The rule mirrors `splash.ts` — only https URLs and plain
 * addresses pass; anything else reads as "not set".
 */
import type { TenantLoginPage } from "./types";
import { ALL_IDENTITY_PROVIDERS, type IdentityProvider } from "@/auth/identity";

export const DEFAULT_SIGN_IN_LABEL = "Sign in";
const MAX_SIGN_IN_LABEL_CHARS = 40;
/** Same shape rules as the backend's login-page.ts: https only, no quotes or
 *  angle brackets, a 2048 cap; an address with no `?&%#/` so it can never
 *  carry a `mailto:` query. */
const MAX_OPTION_URL_CHARS = 2048;
const HTTPS_URL = /^https:\/\/[^\s/$.?#"'<>][^\s"'<>]*$/i;
const EMAIL = /^[^\s@?&%#/"'<>]+@[^\s@?&%#/"'<>]+\.[^\s@?&%#/"'<>]+$/;

export interface PreAuthLink {
  key: "support" | "terms" | "privacy";
  label: string;
  href: string;
}

function httpsOrNull(value: string | null | undefined): string | null {
  const url = value?.trim() ?? "";
  return url.length <= MAX_OPTION_URL_CHARS && HTTPS_URL.test(url) ? url : null;
}

/** The sign-in button's text — the configured label, or the default. */
export function signInLabel(loginPage: TenantLoginPage | null | undefined): string {
  const label = loginPage?.signInLabel?.trim() ?? "";
  return label ? label.slice(0, MAX_SIGN_IN_LABEL_CHARS) : DEFAULT_SIGN_IN_LABEL;
}

/** "Need help?" destination: the URL wins over the email; null when neither is usable. */
export function supportHref(loginPage: TenantLoginPage | null | undefined): string | null {
  const url = httpsOrNull(loginPage?.supportUrl);
  if (url) return url;
  const email = loginPage?.supportEmail?.trim() ?? "";
  return email.length <= MAX_OPTION_URL_CHARS && EMAIL.test(email) ? `mailto:${email}` : null;
}

/** True when the document is a PDF (path ends in .pdf), so the shell can show it in @pdfviewer. */
export function isPdfUrl(url: string): boolean {
  try {
    return new URL(url).pathname.toLowerCase().endsWith(".pdf");
  } catch {
    return false;
  }
}

/** Footer links in display order — support first (the one a stuck visitor needs), then legal. */
export function preAuthLinks(loginPage: TenantLoginPage | null | undefined): PreAuthLink[] {
  const links: PreAuthLink[] = [];
  const support = supportHref(loginPage);
  if (support) links.push({ key: "support", label: "Need help?", href: support });
  const terms = httpsOrNull(loginPage?.termsUrl);
  if (terms) links.push({ key: "terms", label: "Terms", href: terms });
  const privacy = httpsOrNull(loginPage?.privacyUrl);
  if (privacy) links.push({ key: "privacy", label: "Privacy", href: privacy });
  return links;
}

/** The tenant's terms document, or null when it has none that is usable. */
export function termsHref(loginPage: TenantLoginPage | null | undefined): string | null {
  return httpsOrNull(loginPage?.termsUrl);
}

/**
 * The Better Auth methods the sign-in form shows, in display order. Unknown
 * values are dropped. Absent, empty, or nothing known left → all four, so a
 * bad value can never leave a visitor with no way in.
 */
export function identityProviders(loginPage: TenantLoginPage | null | undefined): IdentityProvider[] {
  const wanted = new Set((loginPage?.providers ?? []).map((p) => String(p)));
  const picked = ALL_IDENTITY_PROVIDERS.filter((p) => wanted.has(p));
  return picked.length ? picked : [...ALL_IDENTITY_PROVIDERS];
}

/**
 * The vendor credit. Off when the admin switched it off, and never on an app
 * literally called Metaship — our own front door should not advertise us
 * twice (the rule Login.vue applied before the option existed).
 */
export function showPoweredBy(loginPage: TenantLoginPage | null | undefined, tenantName: string): boolean {
  if (loginPage?.poweredBy === false) return false;
  return tenantName.trim().toLowerCase() !== "metaship";
}
